import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  IndicationSet,
  RD_TYPE_INDICATION,
  RolodexIndication,
} from 'features/indicationTriage'
import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { validateFfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import {
  ClassifiedLta,
  useClassifiedLtas,
} from 'features/libra/stepComponents/ffs/classifiedLtas'
import { useFfsTriageData } from 'features/libra/stepComponents/ffs/useFfsTriageData'
import { useCallback, useMemo, useState } from 'react'
import { GeckoComponent } from 'types'

export interface FfsLtaPrePlanningAggregate {
  totalCount: number
  decidedCount: number
  /** True once every generated LTA carries a classification. */
  allDecided: boolean
}

export interface UseFfsLtaPrePlanningResult {
  ltaSet: IndicationSet | undefined
  indications: ReadonlyArray<RolodexIndication>
  /**
   * Every LTA paired with its parsed classification — the single parse of the
   * transport blob. Consumers must read decisions from here rather than calling
   * `parseRegionClassification` again.
   */
  classifiedLtas: ReadonlyArray<ClassifiedLta>
  isLoading: boolean
  forceRefresh: () => Promise<unknown>
  /** Persist one LTA's classification (validated, then PATCHed onto the entry). */
  saveClassification: (
    indicationId: string,
    classification: FfsRegionClassification,
    opts?: { profileAvailable?: boolean }
  ) => Promise<void>
  isSaving: boolean
  aggregate: FfsLtaPrePlanningAggregate
}

/**
 * Loads the LTA indications for the (component, inspection) and persists each
 * region's classification onto its own indication
 * (`customInfo.ffsRegionClassification`) via `rolodexPatch` (partial-merge —
 * never clobbers the backend-authored LTA
 * fields). Pre-Planning runs AFTER LTA generation, so the indications already
 * exist here. Completeness (D0) = every generated LTA classified.
 */
export function useFfsLtaPrePlanning({
  component,
  inspectionId,
}: {
  component: GeckoComponent | undefined
  inspectionId: string | undefined
}): UseFfsLtaPrePlanningResult {
  const { ltaSet, indications, isLoading, forceRefresh } = useFfsTriageData({
    component,
    inspectionId,
  })
  const classifiedLtas = useClassifiedLtas(indications)
  const { rolodexPatch } = useRolodexEdit()
  const [isSaving, setIsSaving] = useState(false)

  const saveClassification = useCallback(
    async (
      indicationId: string,
      classification: FfsRegionClassification,
      opts?: { profileAvailable?: boolean }
    ) => {
      // Validate the write the same way reads are validated -- enforces the
      // cross-field invariants so an inconsistent record can never be persisted.
      // This is the one caller that must abort, so it raises the message the
      // validator returns.
      const violation = validateFfsRegionClassification(
        classification,
        opts ?? {}
      )
      if (violation) throw new Error(violation)

      // `rolodexPatch` is a plain HTTP PATCH with no client-side merge, and
      // whether the backend merges NESTED objects is not guaranteed -- so send
      // the whole `custom_info` bag, read-modify-write, to preserve sibling keys
      // (notably `ffsStats`, which the corrosion derivation depends on).
      // That makes the current value a hard requirement: writing without it
      // would replace the bag with just this one key and silently drop the rest.
      const current = indications.find((i) => i.id === indicationId)
      if (!current) {
        throw new Error(
          `Cannot save classification: indication ${indicationId} is not in the loaded set.`
        )
      }

      setIsSaving(true)
      try {
        await rolodexPatch([
          {
            id: indicationId,
            type: RD_TYPE_INDICATION,
            data: {
              custom_info: {
                ...current.customInfo,
                ffsRegionClassification: classification,
              },
            },
          },
        ])
        await forceRefresh()
      } finally {
        setIsSaving(false)
      }
    },
    [forceRefresh, indications, rolodexPatch]
  )

  const aggregate = useMemo<FfsLtaPrePlanningAggregate>(() => {
    const totalCount = classifiedLtas.length
    // Only a valid, consistent classification counts as decided (a malformed
    // blob must not pass D0) -- that judgement is already baked into the
    // `classification === null` produced by the single parse.
    const decidedCount = classifiedLtas.filter(
      (l) => l.classification !== null
    ).length
    return {
      totalCount,
      decidedCount,
      allDecided: totalCount > 0 && decidedCount === totalCount,
    }
  }, [classifiedLtas])

  return {
    ltaSet,
    indications,
    classifiedLtas,
    isLoading,
    forceRefresh,
    saveClassification,
    isSaving,
    aggregate,
  }
}
